import { Route, Routes } from '@angular/router';
import { authGuard, guestGuard, rootRedirectGuard } from '@core/guards';
import { environment } from '@environments/environment';

// 0.1: the dev-only UI-kit gallery route — registered outside production only.
const uiKitRoute: Route = {
  path: 'ui-kit',
  loadComponent: () =>
    import('./shared/ui/ui-kit-gallery.component').then(
      (m) => m.UiKitGalleryComponent
    ),
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [rootRedirectGuard],
    children: [], // Empty children since we're redirecting
  },
  {
    path: 'login',
    loadComponent: () => import('./routes/login/login.component').then((m) => m.LoginComponent),
    canActivate: [guestGuard], // Prevent authenticated users from accessing login
  },
  {
    // MFA challenge (task 5.1) — step 2 of password login / mandatory
    // enrollment. Guest-guarded: an authenticated user has no business here.
    // The challenge token arrives via an in-memory bridge, never the URL.
    path: 'mfa-challenge',
    loadComponent: () =>
      import('./routes/mfa-challenge/mfa-challenge.component').then((m) => m.MfaChallengeComponent),
    canActivate: [guestGuard],
  },
  {
    // Account setup (task 5.2) — invite-token driven, must be reachable
    // WITHOUT an existing session (the parent has no account yet).
    //
    // SSO-LINK RESUME: the token value 'sso-resume' is a sentinel, not a
    // real setup token — the MSAL redirect lands at the app root WITHOUT
    // the setup token in the URL (it rode sessionStorage via SsoLinkBridge
    // for exactly one redirect round-trip), and the root guard routes here
    // with 'sso-resume' when a pending link exists. The component detects
    // the sentinel before token validation and switches to resume mode.
    path: 'account-setup/:token',
    loadComponent: () =>
      import('./routes/account-setup/account-setup.component').then((m) => m.AccountSetupComponent),
    canActivate: [guestGuard],
  },
  {
    // Forgot password (task 5.2) — reachable without a session.
    path: 'forgot-password',
    loadComponent: () =>
      import('./routes/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent
      ),
    canActivate: [guestGuard],
  },
  {
    // Reset password (task 5.2) — email-link driven, reachable without a
    // session. The token comes from the route and is cleaned from the URL
    // after consumption.
    path: 'reset-password/:token',
    loadComponent: () =>
      import('./routes/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent
      ),
    canActivate: [guestGuard],
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./routes/dashboard/dashboard.routes').then((m) => m.routes),
    canActivate: [authGuard],
  },
  {
    // Security settings (add-parent-portal-passkey-mfa, task 1.7) —
    // post-login-only MFA management (passkey register/rename/remove +
    // TOTP status). Auth-guarded: a session cookie is required; the
    // backend enforces the same gate plus step-up on remove.
    path: 'security-settings',
    loadComponent: () =>
      import('./routes/security-settings/security-settings.component').then(
        (m) => m.SecuritySettingsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'approvechange',
    loadComponent: () =>
      import('./routes/approvechange/approve-change.component').then(
        (m) => m.ApproveChangeComponent
      ),
    // No auth guard - accessible without login
  },
  {
    // 3.4 (DCMPDEV-699): registration-invite landing. UNGATED — the pending
    // parent has no session yet; the token IS the credential. Mirrors the
    // /approvechange public-route pattern.
    path: 'register/:token',
    loadComponent: () =>
      import('./routes/register-redeem/register-redeem.component').then(
        (m) => m.RegisterRedeemComponent
      ),
  },
  // refresh-parent-portal-ui 0.1: the dev-only UI-kit gallery — every kit
  // component in its variants, to compare against the proposal renders.
  ...(environment.production ? [] : [uiKitRoute]),
  {
    // Catch-all route - redirect to login for unauthenticated users
    path: '**',
    redirectTo: 'login',
  },
];
